import { useState, useCallback } from 'react';

import { DeploymentSelect } from '../Types';

import {
  isMaximumAllowedValueError,
  licenseStatus as fetchLicenseStatus,
  rollbackDeploy as requestRollbackDeploy,
  rollbackDeployDetails
} from '~/api/runtimeService';
import i18n from '~/common/helpers/i18n';
import useAcls from '~/common/hooks/useAcls';
import useSnackbar from '~/common/hooks/useSnackbar';

type UseDeploymentModalsProps = {
  activeRealm: string;
  environment: string;
  environments: Array<string>;
  onDataRefresh: () => Promise<void>;
  create: (
    payload: unknown,
    meta: unknown,
    useV2Api?: boolean
  ) => Promise<void>;
};

function getNameSize(name: string | undefined) {
  return name?.split('-')[0];
}

export const useDeploymentModals = ({
  activeRealm,
  environment,
  environments,
  onDataRefresh,
  create
}: UseDeploymentModalsProps) => {
  const [, containScopes] = useAcls();
  const [deploymentSelected, setDeploymentSelected] =
    useState<DeploymentSelect | null>(null);
  const [isOpenDetailPromote, setIsOpenDetailPromote] = useState(false);
  const [isOpenRollback, setIsOpenRollback] = useState(false);
  const [isDeleteRollback, setIsDeleteRollback] = useState(false);
  const [loadingRollback, setLoadingRollback] = useState(false);

  const snackbar = useSnackbar();

  const openDetailPromote = useCallback(
    async (deployment: DeploymentSelect) => {
      const enviromentsOptions: Array<{ label: string; value: string }> = [];
      environments
        .filter(env => env !== deployment.activeConfiguration?.environment.name)
        .forEach(env => {
          if (containScopes([`DEPLOYMENT:CREATE{ENV=${env.toUpperCase()}}`])) {
            enviromentsOptions.push({
              label: env,
              value: env
            });
          }
        });

      const env = enviromentsOptions[0]?.value;
      let licenseStatus = {};

      if (env && deployment.pipeline?.id) {
        try {
          licenseStatus = await fetchLicenseStatus({
            environment: env,
            realm: activeRealm,
            replicas: deployment.activeConfiguration?.replicaCount,
            pipelineId: deployment.pipeline.id,
            size: getNameSize(deployment.activeConfiguration?.name)
          });
        } catch (error) {
          console.warn('Failed to fetch license status:', error);
        }
      }

      const updatedDeployment: DeploymentSelect = {
        ...deployment,
        newEnvironment: env,
        enviromentsOptions
      };

      if (deployment.pipeline) {
        updatedDeployment.pipeline = {
          ...deployment.pipeline,
          licenseStatus
        };
      }

      setDeploymentSelected(updatedDeployment);

      setIsOpenDetailPromote(true);
    },
    [activeRealm, containScopes, environments]
  );

  const closeDetailPromotion = useCallback(() => {
    setIsOpenDetailPromote(false);
    setDeploymentSelected(null);
  }, []);

  const openModalRollbackDeploy = useCallback(
    async (deployment: DeploymentSelect) => {
      setDeploymentSelected(deployment);
      const details = await rollbackDeployDetails({
        realm: activeRealm,
        deployment: deployment.id,
        environment,
        pipelineName: deployment.pipeline.name,
        pipelineMajorVersion: deployment.pipeline.versionMajor.toString()
      });

      setIsDeleteRollback(!details);
      setIsOpenRollback(true);
    },
    [activeRealm, environment]
  );

  const closeModalRollback = useCallback(() => {
    setIsOpenRollback(false);
    setDeploymentSelected(null);
  }, []);

  const rollbackDeploy = useCallback(async () => {
    if (!deploymentSelected) return;
    setLoadingRollback(true);
    try {
      await requestRollbackDeploy({
        realm: activeRealm,
        deployment: deploymentSelected.id,
        environment,
        pipelineName: deploymentSelected.pipeline.name,
        pipelineMajorVersion:
          deploymentSelected.pipeline.versionMajor.toString()
      });
      snackbar.createSnack(
        i18n.t('label.pipe_deploy_rollback_done_msg_success', {
          pipeName: deploymentSelected.pipeline.name
        })
      );
      closeModalRollback();
      await onDataRefresh();
    } catch (error) {
      if (isMaximumAllowedValueError(error)) {
        snackbar.createSnack(
          i18n.t('label.maximum_replicas_allowed_error', {
            maxReplicas: error.message.split(':')[1]?.trim()
          })
        );
      } else {
        snackbar.createSnack(
          i18n.t('label.pipe_deploy_rollback_failed_msg_alert', {
            pipeName: deploymentSelected.pipeline.name
          })
        );
      }
    } finally {
      setLoadingRollback(false);
    }
  }, [
    deploymentSelected,
    activeRealm,
    environment,
    snackbar,
    closeModalRollback,
    onDataRefresh
  ]);

  const handlePromoteConfirm = useCallback(
    async (promotionData: any) => {
      try {
        await create(promotionData, {
          deployment: deploymentSelected,
          environment
        });
        closeDetailPromotion();
        await onDataRefresh();
      } catch (error) {
        snackbar.createSnack(i18n.t('scenes.runtime.messages.promote_error'));
      }
    },
    [
      create,
      deploymentSelected,
      environment,
      closeDetailPromotion,
      onDataRefresh,
      snackbar
    ]
  );

  return {
    deploymentSelected,
    isOpenDetailPromote,
    isOpenRollback,
    loadingRollback,
    isDeleteRollback,

    openDetailPromote,
    closeDetailPromotion,
    openModalRollbackDeploy,
    closeModalRollback,
    rollbackDeploy,
    handlePromoteConfirm
  };
};
